<template>
	<div :class="card ? 'recent-docs-card' : 'recent-docs-list'">

		<!-- Header (card mode only) -->
		<div v-if="card" class="card-header">
			<p class="card-title">Recently added</p>
			<div class="header-divider"></div>
		</div>

		<!-- Empty state -->
		<p v-if="limitedGroups.length === 0" class="empty-text">No documents yet</p>

		<!-- Grouped document rows -->
		<template v-for="group in limitedGroups" :key="group.dateLabel">
			<p class="date-label">{{ group.dateLabel }} ({{ group.docs.length }})</p>
			<div
				v-for="doc in group.docs"
				:key="doc.id"
				class="doc-row"
				@click="$emit('doc-click', doc)"
			>
				<div class="doc-info">
					<p class="doc-company">{{ doc.company || doc.documentSubject }}</p>
					<p class="doc-filename">{{ doc.documentName }}</p>
					<p class="doc-added">Added {{ formatDate(doc.createDateTime) }}</p>
					<p v-if="doc.documentSubject" class="doc-label">{{ doc.documentSubject }}</p>
				</div>
				<img :src="require('@/assets/arrow-right.png')" class="doc-arrow" />
			</div>
		</template>

		<!-- View all footer -->
		<div v-if="showViewAll" class="view-all-row" @click="$emit('view-more')">
			<span class="view-all-text">View all</span>
			<img :src="require('@/assets/arrow-right.png')" class="view-all-arrow" />
		</div>
	</div>
</template>

<script>
export default {
	name: 'RecentDocuments',
	emits: ['doc-click', 'view-more'],
	props: {
		documents: { type: Array, required: true },
		limit: { type: Number, default: null },
		card: { type: Boolean, default: true },
	},
	computed: {
		sorted() {
			return [...this.documents].sort((a, b) => new Date(b.createDateTime) - new Date(a.createDateTime));
		},
		limitedGroups() {
			const docs = this.limit ? this.sorted.slice(0, this.limit) : this.sorted;

			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const yesterday = new Date(today);
			yesterday.setDate(yesterday.getDate() - 1);

			const groups = [];
			const map = {};

			docs.forEach(doc => {
				const d = new Date(doc.createDateTime);
				d.setHours(0, 0, 0, 0);

				let label;
				if (d.getTime() === today.getTime()) label = 'Today';
				else if (d.getTime() === yesterday.getTime()) label = 'Yesterday';
				else label = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

				if (!map[label]) { map[label] = { dateLabel: label, docs: [] }; groups.push(map[label]); }
				map[label].docs.push(doc);
			});

			return groups;
		},
		showViewAll() {
			return this.card && this.limit && this.documents.length > this.limit;
		},
	},
	methods: {
		formatDate(dateStr) {
			if (!dateStr) return '';
			const d = new Date(dateStr);
			return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
		},
	},
};
</script>

<style scoped>
/* Card mode wrapper */
.recent-docs-card {
	background: white;
	border-radius: 12px;
	padding: 16px 16px 20px;
	display: flex;
	flex-direction: column;
	gap: 16px;
}

/* List mode (drawer) */
.recent-docs-list {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding-bottom: 40px;
}

/* Header */
.card-header {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.card-title {
	font-size: 15px;
	font-weight: bold;
	color: var(--brand-purple);
	margin: 0;
}

.header-divider {
	height: 1px;
	background: rgba(65, 54, 85, 0.1);
}

/* Date label */
.date-label {
	font-size: 14px;
	font-weight: bold;
	color: var(--brand-purple);
	margin: 0;
}

/* Document row */
.doc-row {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 12px 16px;
	background: white;
	border-radius: 8px;
	border: 1px solid rgba(65, 54, 85, 0.1);
	box-shadow: 0px 4px 8px 0px rgba(51, 40, 71, 0.05);
}

.doc-info {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.doc-info p {
	margin: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.doc-company {
	font-size: 13px;
	font-weight: bold;
	color: var(--brand-purple);
}

.doc-filename {
	font-size: 12px;
	color: var(--brand-purple);
}

.doc-added {
	font-size: 12px;
	color: var(--brand-purple);
	opacity: 0.5;
}

.doc-label {
	font-size: 11px;
	font-weight: bold;
	color: var(--brand-purple);
}

.doc-arrow {
	width: 16px;
	height: 12px;
	flex-shrink: 0;
}

/* View all footer */
.view-all-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.view-all-text {
	font-size: 13px;
	color: var(--brand-purple);
}

.view-all-arrow {
	width: 16px;
	height: 10px;
}

.empty-text {
	font-size: 13px;
	color: var(--light-text-secondary);
	margin: 0;
	text-align: center;
}
</style>
